import { Link } from "react-router";
import type { StoreOffersReuse, StoreOffersStatus } from "@townsy/contracts";
import { isApiError } from "../../../api/client";
import { isRewardsUnavailable } from "../../../api/rewards";
import { useStoreOffersQuery } from "../../../api/storeOffers";
import { offersStatusCopy, reuseQuestion } from "../../../rewards/storeOffersModel";
import { useTownStore, type OfferSetupRequestUi } from "../../../state/store";
import { OffersStatusBanner } from "./OfferSetupDialog";

/**
 * The ways into "Add an offer" / "Edit offers" (ADR-0027): the owner's store panel (the sponsored tasks section, with the
 * one plain status, and a button next to "Edit your store"), the directory's "My stores", the advertiser page and the
 * owner's notices. Offered only to the current owner (their own property read lists `edit_interior`, the server's
 * current-owner rule); the server re-checks everything.
 */

/** ADR-0029: offers are free for building owners (Townsy gives the coins). */
export const OFFERS_WHAT = "Free for you: visitors earn coins from Townsy for visiting your website or answering a question about your store.";

/** The owner's card in the store panel's sponsored tasks section: the status and the button (server-rendered in tests). */
export function StoreOffersPanelView({
  status,
  storeLive,
  onOpen,
  reuse = null,
  onReuse,
}: {
  status: Pick<StoreOffersStatus, "state" | "note" | "completions" | "coinsGiven" | "autoStart"> | null;
  /** The store's design is live (offers are built on it). */
  storeLive: boolean;
  onOpen(): void;
  /** ADR-0030: the owner's own earlier offers this store can re-use (the server sends them only while none run here). */
  reuse?: Pick<StoreOffersReuse, "fromBusinessName" | "fromAddress"> | null;
  onReuse?(): void;
}) {
  const copy = status ? offersStatusCopy(status) : null;
  const none = !status || status.state === "none" || status.state === "no_offers";
  return (
    <div className="os-panel" data-testid="offer-setup-panel">
      {status && copy ? <OffersStatusBanner status={status} copy={copy} compact /> : <p className="small muted" style={{ margin: 0 }}>Checking your offers…</p>}
      {reuse ? (
        <p className="small" style={{ margin: 0 }} data-testid="offer-setup-panel-reuse">
          <strong>{reuseQuestion(reuse)}</strong> {storeLive ? "One press fills in the offer screen; you check them and send them for review." : "Set up your store first, then re-use them here."}
        </p>
      ) : null}
      <div className="row os-panel-actions">
        {reuse && storeLive && onReuse ? (
          <button type="button" className="btn btn-primary" onClick={() => onReuse()} data-testid="offer-setup-panel-reuse-button">
            Re-use your offers
          </button>
        ) : null}
        <button type="button" className={reuse && storeLive && onReuse ? "btn" : "btn btn-primary"} onClick={() => onOpen()} data-testid="add-an-offer">
          {none ? "Add an offer" : "Edit offers"}
        </button>
      </div>
      <span className="hint">
        {storeLive || !none ? OFFERS_WHAT : "Offers use your store's name, logo and website: set up your store first, then add an offer."}{" "}
        You can't earn coins from your own offers.
      </span>
    </div>
  );
}

/** The store panel's owner card (only for the current owner; nothing when sponsored tasks are off on this server). */
export function StoreOffersPanelSection({ propertyId, isOwner }: { propertyId: string; isOwner: boolean }) {
  const q = useStoreOffersQuery(isOwner ? propertyId : null);
  const open = useTownStore((s) => s.openOfferSetup);
  // Not shown when sponsored tasks are off on this server, or when the read says this account is not the owner after all.
  if (!isOwner || (q.error && (isRewardsUnavailable(q.error) || (isApiError(q.error) && q.error.status === 403)))) return null;
  return (
    <StoreOffersPanelView
      status={q.data?.status ?? null}
      storeLive={Boolean(q.data?.store)}
      onOpen={() => open(propertyId, "panel")}
      reuse={q.data?.reuse ?? null}
      onReuse={() => open(propertyId, "panel", "reuse")}
    />
  );
}

/** A small "Add an offer" / "Edit offers" button (next to "Edit your store", the directory's "My stores"). */
export function OffersButton({ propertyId, source, hasOffers, className = "btn btn-sm" }: { propertyId: string; source: OfferSetupRequestUi["source"]; hasOffers: boolean; className?: string }) {
  const open = useTownStore((s) => s.openOfferSetup);
  return (
    <button type="button" className={className} onClick={() => open(propertyId, source)} data-testid={`offers-open-${propertyId}`}>
      {hasOffers ? "Edit offers" : "Add an offer"}
    </button>
  );
}

/** The same button, labelled from the store's offers (hidden when sponsored tasks are off on this server). */
export function MyStoreOffersButton({ propertyId, source = "directory" }: { propertyId: string; source?: OfferSetupRequestUi["source"] }) {
  const q = useStoreOffersQuery(propertyId);
  if (q.error && isRewardsUnavailable(q.error)) return null;
  return <OffersButton propertyId={propertyId} source={source} hasOffers={Boolean(q.data?.campaign)} />;
}

/** The advertiser page's pointer to the simpler screen. */
export function OffersShortcut({ stores }: { stores: ReadonlyArray<{ id: string; label: string }> }) {
  if (stores.length === 0) return null;
  return (
    <div className="notice notice-info small os-shortcut" data-testid="offers-shortcut">
      <strong>Simpler: add an offer on one screen.</strong> Pick what visitors do and a reward; it starts by itself once it passes review. Offers are free for building owners: Townsy gives the coins.
      <ul className="os-shortcut-list">
        {stores.map((s) => (
          <li key={s.id} className="row">
            <span>{s.label}</span>
            <MyStoreOffersButton propertyId={s.id} source="advertiser" />
          </li>
        ))}
      </ul>
      <Link to="/directory#my-stores" className="small">
        All my stores
      </Link>
    </div>
  );
}
